/**
 * Session state for the whole app: who is signed in, and the sign-in /
 * sign-up / sign-out actions. Replaces Base44's hosted login.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { User } from '@eventflow/shared';

import { api } from '@/api/client';
import { ApiError } from '@/api/http';

type Status = 'loading' | 'anon' | 'authed';

interface AuthValue {
  user: User | null;
  status: Status;
  isAdmin: boolean;
  isAuthenticated: boolean;
  isLoadingAuth: boolean;
  login: (email: string, password: string) => Promise<User>;
  signup: (body: { email: string; password: string; full_name: string }) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<User | null>;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [status, setStatus] = useState<Status>('loading');

  const setUser = useCallback((next: User | null) => {
    setUserState(next);
    setStatus(next ? 'authed' : 'anon');
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.auth.me();
      setUser(me);
      return me;
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        setUser(null);
        return null;
      }
      // Network trouble: stay anonymous rather than spin forever.
      setUser(null);
      return null;
    }
  }, [setUser]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onUnauthorized = () => setUser(null);
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, [setUser]);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      status,
      isAdmin: user?.role === 'admin',
      isAuthenticated: status === 'authed',
      isLoadingAuth: status === 'loading',
      login: async (email, password) => {
        const me = await api.auth.login(email, password);
        setUser(me);
        return me;
      },
      signup: async (body) => {
        const result = await api.auth.signup(body);
        setUser(result.user);
        return result.user;
      },
      logout: async () => {
        try {
          await api.auth.logout();
        } finally {
          setUser(null);
        }
      },
      refresh,
      setUser,
    }),
    [user, status, refresh, setUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
